import { createServerFn } from "@tanstack/react-start";
import { requireAuth } from "@/lib/auth/middleware";
import { requireAdmin } from "@/lib/auth/middleware";
import * as db from "@/lib/db.server";

/**
 * Server fns for the notification-count bell hook. Split out of the hook file so
 * the hook module does not statically import db.server / access.server at top
 * level (which keeps the `.middleware([requireAdmin])` reference alive in the
 * eager client bundle and trips TanStack import-protection). Imported only by
 * the hook → resolved into the server bundle.
 *
 * No `count:"exact"` — uses bounded `LIMIT CAP` to keep the bell snappy
 * and never scan whole tables. Anything at the cap displays as "CAP+".
 *
 * The Aurora reads run in TanStack server functions (the browser can't reach
 * Aurora). Admin counts are gated by `requireAdmin`; the doctor's own claims
 * are gated by `requireAuth` and scoped to the authenticated `userId`.
 */
export const NOTIFICATION_CAP = 99;
const FETCH_LIMIT = NOTIFICATION_CAP + 1;

/**
 * Admin notification counts across ALL records (gated by `requireAdmin`, so
 * no owner filter). Each subquery is bounded by `FETCH_LIMIT` and we count the
 * rows returned so anything at the cap renders as "CAP+".
 */
export const loadAdminNotificationCountsFn = createServerFn({ method: "GET" })
  .middleware([requireAdmin])
  .handler(async () => {
    const [claims, requests, alerts] = await Promise.all([
      db.query(
        "SELECT id FROM public.claims WHERE status = $1 LIMIT $2",
        ["pending", FETCH_LIMIT],
      ),
      db.query(
        "SELECT id FROM public.provider_update_requests WHERE status = $1 LIMIT $2",
        ["pending", FETCH_LIMIT],
      ),
      db.query(
        "SELECT id FROM public.admin_claim_alerts WHERE acknowledged_at IS NULL LIMIT $1",
        [FETCH_LIMIT],
      ),
    ]);
    return { claims: claims.length, requests: requests.length, alerts: alerts.length };
  });

/**
 * Doctor notification counts. Claims are scoped to the authenticated user
 * (`context.userId`); pending update requests mirror the original client read
 * (status-only, no owner column on that table).
 */
export const loadDoctorNotificationCountsFn = createServerFn({ method: "GET" })
  .middleware([requireAuth])
  .handler(async ({ context }) => {
    const [claims, requests] = await Promise.all([
      db.query(
        "SELECT id FROM public.claims WHERE user_id = $1 AND status = $2 LIMIT $3",
        [context.userId, "pending", FETCH_LIMIT],
      ),
      db.query(
        "SELECT id FROM public.provider_update_requests WHERE status = $1 LIMIT $2",
        ["pending", FETCH_LIMIT],
      ),
    ]);
    return { claims: claims.length, requests: requests.length };
  });
